/**
 * Smart guard-rails for live scoring (Persona #1, Feature 1).
 *
 * Watches the recent-balls stream and the current in-progress ball,
 * surfaces non-blocking hints when the pattern looks unusual. Operator
 * one-taps to dismiss; nothing is auto-corrected. False positives are
 * tolerable (mild noise); false negatives are the failure mode this
 * is designed to fix (silent corruption that survives to the final
 * scorecard).
 *
 * Rules currently checked:
 *   1. RUN_BURST   — 3+ identical 4/6 outcomes in a row from the same
 *                    striker. Real 6-6-6-6 happens, but it's rare —
 *                    one verify prompt is cheap insurance.
 *   2. STROKE_RUT  — 5+ identical strokes in a row. Likely operator
 *                    forgot to update the stroke chip between balls.
 *   3. NO_ROTATION — striker unchanged for 12+ balls with no wicket
 *                    AND no in-between rotation. Statistically very
 *                    rare; usually means the operator forgot to
 *                    swap strikes after an odd-run ball.
 *   4. DOT_STORM   — 8+ dot balls in a row. Possible (good bowling)
 *                    but also a common operator slip (forgot to
 *                    enter runs). Worth a verify.
 *
 * Each warning has a stable key so the SPA can persist dismissals
 * and not re-prompt for the same situation immediately.
 */
import { useMemo } from "react";


const DISMISSAL_KEY_PREFIX = "zcd_guardrail_dismissed_";


function loadDismissed(matchId) {
  try {
    const raw = localStorage.getItem(DISMISSAL_KEY_PREFIX + matchId);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}


export function dismissGuardRail(matchId, key) {
  try {
    const set = loadDismissed(matchId);
    set.add(key);
    localStorage.setItem(
      DISMISSAL_KEY_PREFIX + matchId,
      JSON.stringify([...set]),
    );
  } catch { /* quota */ }
}


function clamp(arr, n) {
  return arr.slice(Math.max(0, arr.length - n));
}


export function useScoringGuardRails({ matchId, recentBalls, dismissTick = 0 }) {
  // dismissTick is a caller-controlled cache-buster: bump it on every
  // dismiss so the memo re-evaluates and the dismissed warning drops
  // out of the returned list immediately. Without it the memo's deps
  // are unchanged and the warning sticks until the next ball event.
  return useMemo(() => {
    if (!Array.isArray(recentBalls) || recentBalls.length === 0) return [];
    const dismissed = matchId ? loadDismissed(matchId) : new Set();
    const warnings = [];

    // ── RUN_BURST: 3+ identical boundary outcomes from same striker ──
    // Tracks ONLY legal deliveries. A wide/no-ball six doesn't count
    // against the streak (different cricket context).
    const legals = recentBalls.filter((b) => b.is_legal);
    const last4 = clamp(legals, 4);
    if (last4.length >= 3) {
      const allSix = last4.length >= 3 && last4.slice(-3).every(
        (b) => b.runs_off_bat === 6
          && b.striker
          && b.striker === last4[last4.length - 1].striker,
      );
      const allFour = last4.length >= 4 && last4.slice(-4).every(
        (b) => b.runs_off_bat === 4
          && b.striker
          && b.striker === last4[last4.length - 1].striker,
      );
      if (allSix) {
        const key = `runburst_six_${last4[last4.length - 1].id}`;
        if (!dismissed.has(key)) {
          warnings.push({
            key,
            level: "warn",
            text: `3 sixes in a row by ${last4[last4.length - 1].striker} — verify?`,
            hint: "Real but rare. Tap to dismiss if correct.",
          });
        }
      } else if (allFour) {
        const key = `runburst_four_${last4[last4.length - 1].id}`;
        if (!dismissed.has(key)) {
          warnings.push({
            key,
            level: "warn",
            text: `4 fours in a row by ${last4[last4.length - 1].striker} — verify?`,
            hint: "Possible; just confirming.",
          });
        }
      }
    }

    // ── STROKE_RUT: same stroke 5+ legal balls in a row ─────────────
    // Common operator slip: clicked stroke once, forgot to change
    // between balls. Only fires when stroke is set on all balls in
    // the window (a null breaks the streak — operator was already
    // varying).
    if (legals.length >= 5) {
      const last5 = clamp(legals, 5);
      const stroke0 = last5[0].analytics?.stroke || last5[0].stroke;
      if (stroke0 && last5.every((b) => (b.analytics?.stroke || b.stroke) === stroke0)) {
        const key = `strokerut_${stroke0}_${last5[last5.length - 1].id}`;
        if (!dismissed.has(key)) {
          warnings.push({
            key,
            level: "info",
            text: `Stroke "${stroke0}" recorded 5 balls running — switch?`,
            hint: "Common slip — operator forgot to update the chip.",
          });
        }
      }
    }

    // ── NO_ROTATION: striker unchanged 12+ balls, no wicket ─────────
    // If the same striker faces 12 legal deliveries without rotation
    // AND no wicket fell, the operator probably missed strike swaps
    // on odd-run balls or end-of-over.
    if (legals.length >= 12) {
      const last12 = clamp(legals, 12);
      const striker = last12[0].striker;
      const sameStriker = striker && last12.every((b) => b.striker === striker);
      const noWicket = last12.every((b) => !b.wicket);
      if (sameStriker && noWicket) {
        const key = `norotation_${striker}_${last12[last12.length - 1].id}`;
        if (!dismissed.has(key)) {
          warnings.push({
            key,
            level: "warn",
            text: `${striker} on strike 12 balls running — check rotation?`,
            hint: "Strike swaps on odd-run balls + at over end. Verify.",
          });
        }
      }
    }

    // ── DOT_STORM: 8+ dot balls in a row ────────────────────────────
    if (legals.length >= 8) {
      const last8 = clamp(legals, 8);
      const allDots = last8.every(
        (b) => (b.runs_off_bat ?? 0) === 0
          && (b.extras ?? 0) === 0
          && !b.wicket,
      );
      if (allDots) {
        const key = `dotstorm_${last8[last8.length - 1].id}`;
        if (!dismissed.has(key)) {
          warnings.push({
            key,
            level: "info",
            text: "8 dot balls in a row — verify runs weren't missed",
            hint: "Possible maiden+; just confirming.",
          });
        }
      }
    }

    return warnings;
  }, [matchId, recentBalls, dismissTick]);
}
